import React, { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import './HRAttendanceNew.css';

const HRAttendanceNew = () => {
  const user = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('attendance');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, onLeave: 0, avgWorkHours: 0 });
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [myAttendance, setMyAttendance] = useState(null);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [leaveView, setLeaveView] = useState('my');

  useEffect(() => {
    fetchEmployees();
    fetchStats();
    fetchAttendance();
    fetchLeaves();
    fetchHolidays();
    fetchMyAttendance();
  }, [selectedDate, selectedEmployee]);

  const fetchMyAttendance = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.get(`/time/attendance/my?date=${today}`);
      if (res.data.data) {
        setMyAttendance(res.data.data);
        setIsPunchedIn(res.data.data.punchInTime && !res.data.data.punchOutTime);
      }
    } catch (err) {
      console.error('Error fetching my attendance:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/users');
      setEmployees(res.data.data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`/attendance/stats?date=${selectedDate}`);
      setStats(res.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAttendance = async () => {
    try {
      const url = selectedEmployee === 'all'
        ? `/attendance/today?date=${selectedDate}`
        : `/attendance/monthly?date=${selectedDate}&employee=${selectedEmployee}`;
      const res = await api.get(url);
      setAttendanceRecords(res.data.data || []);
    } catch (err) {
      console.error('Error fetching attendance:', err);
    }
  };

  const fetchLeaves = async () => {
    try {
      const res = await api.get('/leaves/all');
      setLeaves(res.data.data || []);
      calculateLeaveBalance(res.data.data || []);
    } catch (err) {
      console.error('Error fetching leaves:', err);
    }
  };

  const calculateLeaveBalance = (leaveData) => {
    const balance = {
      'Sick Leave': { total: 10, used: 0, remaining: 10 },
      'Casual Leave': { total: 12, used: 0, remaining: 12 },
      'Vacation Leave': { total: 15, used: 0, remaining: 15 }
    };

    leaveData
      .filter(l => l.status === 'Approved' && String(l.user?._id) === String(user?.id || user?._id))
      .forEach(leave => {
        if (balance[leave.leaveType]) {
          balance[leave.leaveType].used += leave.days;
          balance[leave.leaveType].remaining = balance[leave.leaveType].total - balance[leave.leaveType].used;
        }
      });

    setLeaveBalance(balance);
  };

  const fetchHolidays = async () => {
    try {
      const year = new Date(selectedDate).getFullYear();
      const res = await api.get(`/holidays?year=${year}`);
      setHolidays(res.data.data || []);
    } catch (err) {
      console.error('Error fetching holidays:', err);
    }
  };

  const handleMarkAttendance = async (formData) => {
    try {
      setLoading(true);
      await api.post('/attendance/mark', formData);
      setShowMarkModal(false);
      fetchStats();
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error marking attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (leaveId, status) => {
    try {
      await api.put(`/leaves/${leaveId}/status`, { status });
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating leave');
    }
  };

  const handleAddHoliday = async (formData) => {
    try {
      setLoading(true);
      await api.post('/holidays', formData);
      setShowHolidayModal(false);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error adding holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (formData) => {
    try {
      setLoading(true);
      await api.post('/leaves/apply', formData);
      toast.success('Leave applied successfully!');
      setShowLeaveModal(false);
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error applying leave');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await api.delete(`/holidays/${id}`);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting holiday');
    }
  };

  const handleEditAttendance = (record) => {
    setEditRecord({
      ...record,
      punchInTime: record.punchInTime ? new Date(record.punchInTime).toTimeString().slice(0, 5) : '',
      punchOutTime: record.punchOutTime ? new Date(record.punchOutTime).toTimeString().slice(0, 5) : ''
    });
    setShowEditModal(true);
  };

  const handleUpdateAttendance = async (formData) => {
    try {
      setLoading(true);
      await api.put(`/attendance/${editRecord._id}`, formData);
      setShowEditModal(false);
      setEditRecord(null);
      fetchAttendance();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm('Delete this attendance record?')) return;
    try {
      await api.delete(`/attendance/${id}`);
      fetchAttendance();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting attendance');
    }
  };

  const handlePunchIn = async () => {
    try {
      setLoading(true);
      await api.post('/time/attendance/punch-in');
      toast.success('Punched in successfully!');
      fetchMyAttendance();
      fetchStats();
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error punching in');
    } finally {
      setLoading(false);
    }
  };

  const handlePunchOut = async () => {
    try {
      setLoading(true);
      await api.post('/time/attendance/punch-out');
      toast.success('Punched out successfully!');
      fetchMyAttendance();
      fetchStats();
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error punching out');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      let endpoint = '';

      if (activeTab === 'attendance') {
        const params = selectedEmployee === 'all'
          ? `date=${selectedDate}`
          : `date=${selectedDate}&employee=${selectedEmployee}`;
        endpoint = `/attendance/export?${params}`;
      } else if (activeTab === 'leave') {
        if (leaveView === 'my') {
          endpoint = '/leaves/export/my';
        } else {
          if (user?.role !== 'HR') {
            toast.error('Only HR can export all leaves');
            return;
          }
          endpoint = '/leaves/export/all';
        }
      } else {
        toast.info('Export not available for this tab');
        return;
      }

      // Append token for server-side auth (window.open doesn't send headers)
      const exportUrl = `${baseUrl}${endpoint}${endpoint.includes('?') ? '&' : '?'}token=${token}`;
      window.open(exportUrl, '_blank');
    } catch (err) {
      toast.error('Error exporting data');
    }
  };

  return (
    <Layout>
      <div className="hr-attendance-container">
        <div className="hr-attendance-header">
          <div className="header-left">
            <h1 className="page-title">Attendance Management</h1>
            <p className="page-subtitle">Track and manage employee attendance</p>
          </div>
          <div className="header-actions">
            <button className="btn-export" onClick={handleExport}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              Export
            </button>
            {activeTab === 'attendance' && (
              <button className="btn-primary" onClick={() => setShowMarkModal(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Mark Attendance
              </button>
            )}
            {activeTab === 'leave' && (
              <button className="btn-primary" onClick={() => setShowLeaveModal(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Apply Leave
              </button>
            )}
            {activeTab === 'holidays' && (
              <button className="btn-primary" onClick={() => setShowHolidayModal(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {activeTab === 'attendance' && (
          <div className="my-attendance-card">
            <div className="card-header-row">
              <h3 className="card-title">My Attendance Today</h3>
              {!isPunchedIn ? (
                <button className="btn-punch-in" onClick={handlePunchIn} disabled={loading}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                  Punch In
                </button>
              ) : (
                <button className="btn-punch-out" onClick={handlePunchOut} disabled={loading}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  Punch Out
                </button>
              )}
            </div>
            {myAttendance && (
              <div className="attendance-details">
                <div className="detail-item">
                  <span className="detail-label">Punch In:</span>
                  <span className="detail-value">{myAttendance.punchInTime ? new Date(myAttendance.punchInTime).toLocaleTimeString() : '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Punch Out:</span>
                  <span className="detail-value">{myAttendance.punchOutTime ? new Date(myAttendance.punchOutTime).toLocaleTimeString() : '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Work Hours:</span>
                  <span className="detail-value">{myAttendance.workMinutes ? (myAttendance.workMinutes / 60).toFixed(1) + 'h' : '—'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card stat-present">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.present}</div>
              <div className="stat-label">PRESENT</div>
            </div>
          </div>
          <div className="stat-card stat-absent">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.absent}</div>
              <div className="stat-label">ABSENT</div>
            </div>
          </div>
          <div className="stat-card stat-late">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.late}</div>
              <div className="stat-label">LATE ARRIVALS</div>
            </div>
          </div>
          <div className="stat-card stat-leave">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.onLeave}</div>
              <div className="stat-label">ON LEAVE</div>
            </div>
          </div>
          <div className="stat-card stat-hours">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.avgWorkHours}h</div>
              <div className="stat-label">AVG WORK HOURS</div>
            </div>
          </div>
        </div>

        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            Attendance
          </button>
          <button
            className={`tab-btn ${activeTab === 'leave' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave')}
          >
            Leave Management
          </button>
          <button
            className={`tab-btn ${activeTab === 'holidays' ? 'active' : ''}`}
            onClick={() => setActiveTab('holidays')}
          >
            Holidays
          </button>
        </div>

        {activeTab === 'attendance' && (
          <AttendanceTab
            records={attendanceRecords}
            onEdit={handleEditAttendance}
            onDelete={handleDeleteAttendance}
          />
        )}

        {activeTab === 'leave' && (
          <LeaveTab
            leaves={leaves}
            balance={leaveBalance}
            onApprove={handleApproveLeave}
            leaveView={leaveView}
            setLeaveView={setLeaveView}
          />
        )}

        {activeTab === 'holidays' && (
          <HolidaysTab
            holidays={holidays}
            onDelete={handleDeleteHoliday}
          />
        )}

        {showMarkModal && (
          <MarkAttendanceModal
            employees={employees}
            selectedDate={selectedDate}
            onClose={() => setShowMarkModal(false)}
            onSubmit={handleMarkAttendance}
            loading={loading}
          />
        )}

        {showLeaveModal && (
          <ApplyLeaveModal
            onClose={() => setShowLeaveModal(false)}
            onSubmit={handleApplyLeave}
            loading={loading}
          />
        )}

        {showHolidayModal && (
          <HolidayModal
            onClose={() => setShowHolidayModal(false)}
            onSubmit={handleAddHoliday}
            loading={loading}
          />
        )}

        {showEditModal && editRecord && (
          <EditAttendanceModal
            record={editRecord}
            onClose={() => {
              setShowEditModal(false);
              setEditRecord(null);
            }}
            onSubmit={handleUpdateAttendance}
            loading={loading}
          />
        )}
      </div>
    </Layout>
  );
};

const AttendanceTab = ({ records, onEdit, onDelete }) => (
  <div className="attendance-table-container">
    <table className="attendance-table">
      <thead>
        <tr>
          <th>Employee</th>
          <th>Date</th>
          <th>Status</th>
          <th>Punch In</th>
          <th>Punch Out</th>
          <th>Work Hours</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {records.length === 0 ? (
          <tr><td colSpan="7" className="no-data">No attendance records found</td></tr>
        ) : (
          records.map(record => (
            <tr key={record._id}>
              <td>{record.user?.name || 'N/A'}</td>
              <td>{new Date(record.date).toLocaleDateString()}</td>
              <td>
                <span className={`status-badge status-${record.status.toLowerCase().replace(' ', '-')}`}>
                  {record.status}
                </span>
              </td>
              <td>{record.punchInTime ? new Date(record.punchInTime).toLocaleTimeString() : '-'}</td>
              <td>{record.punchOutTime ? new Date(record.punchOutTime).toLocaleTimeString() : '-'}</td>
              <td>{record.workMinutes ? (record.workMinutes / 60).toFixed(1) + 'h' : '-'}</td>
              <td>
                <button className="btn-icon" onClick={() => onEdit(record)} title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button className="btn-icon" onClick={() => onDelete(record._id)} title="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const LeaveTab = ({ leaves, balance, onApprove, leaveView, setLeaveView }) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const [selectedRoles, setSelectedRoles] = React.useState([]);
  const [selectedEmployees, setSelectedEmployees] = React.useState([]);
  const [employeeSearch, setEmployeeSearch] = React.useState('');
  const user = useAuthStore((state) => state.user);
  const filterRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  const roles = ['HR', 'Operations Head', 'Project Manager', 'Project Lead', 'Developer', 'QA Engineer', 'UI Engineer'];

  const uniqueEmployees = [...new Map(leaves.map(leave =>
    [leave.user?._id, { id: leave.user?._id, name: leave.user?.name, role: leave.user?.role }]
  )).values()].filter(emp => emp.id);

  const filteredEmployeeList = uniqueEmployees.filter(emp =>
    emp.name?.toLowerCase().includes(employeeSearch.toLowerCase())
  );

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleEmployeeToggle = (empId) => {
    setSelectedEmployees(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const clearFilters = () => {
    setSelectedRoles([]);
    setSelectedEmployees([]);
    setEmployeeSearch('');
  };

  let filteredLeaves = leaveView === 'my'
    ? leaves.filter(leave => String(leave.user?._id) === String(user?.id || user?._id))
    : leaves;

  if (leaveView === 'all') {
    if (selectedRoles.length > 0) {
      filteredLeaves = filteredLeaves.filter(leave => selectedRoles.includes(leave.user?.role));
    }
    if (selectedEmployees.length > 0) {
      filteredLeaves = filteredLeaves.filter(leave => selectedEmployees.includes(leave.user?._id));
    }
  }

  return (
    <div className="leave-container">
      <div className="leave-balance-grid">
        {Object.entries(balance).map(([type, data]) => (
          <div key={type} className="leave-balance-card">
            <h4 className="leave-type">{type}</h4>
            <div className="leave-stats">
              <div className="leave-stat">
                <span className="leave-stat-label">TOTAL</span>
                <span className="leave-stat-value">{data.total}</span>
              </div>
              <div className="leave-stat">
                <span className="leave-stat-label">USED</span>
                <span className="leave-stat-value used">{data.used}</span>
              </div>
              <div className="leave-stat">
                <span className="leave-stat-label">REMAINING</span>
                <span className="leave-stat-value remaining">{data.remaining}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="leave-tabs">
        <button
          className={`leave-tab-btn ${leaveView === 'my' ? 'active' : ''}`}
          onClick={() => { setLeaveView('my'); setShowFilters(false); }}
        >
          My Leaves
        </button>
        <button
          className={`leave-tab-btn ${leaveView === 'all' ? 'active' : ''}`}
          onClick={() => { setLeaveView('all'); setShowFilters(false); }}
        >
          All Leaves
        </button>
      </div>

      <div className="leave-table-container">
        <div className="leave-table-header">
          <h3 className="leave-table-title">{leaveView === 'my' ? 'My Leave Records' : 'All Leave Records'}</h3>
          {leaveView === 'all' && (
            <div className="filter-icon-wrapper" ref={filterRef}>
              <button className="filter-icon-btn" onClick={() => setShowFilters(!showFilters)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                  <circle cx="7" cy="6" r="2" fill="currentColor" />
                  <circle cx="14" cy="12" r="2" fill="currentColor" />
                  <circle cx="17" cy="18" r="2" fill="currentColor" />
                </svg>
                Filter
              </button>
              {showFilters && (
                <div className="filter-dropdown">
                  <div className="filter-section">
                    <label className="filter-section-label">Filter by Role</label>
                    <div className="filter-options">
                      {roles.map(role => (
                        <label key={role} className="filter-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role)}
                            onChange={() => handleRoleToggle(role)}
                          />
                          <span>{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="filter-section">
                    <label className="filter-section-label">Filter by Employee</label>
                    <input
                      type="text"
                      className="filter-search-input"
                      placeholder="Search employee..."
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                    />
                    <div className="filter-options scrollable">
                      {filteredEmployeeList.map(emp => (
                        <label key={emp.id} className="filter-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={() => handleEmployeeToggle(emp.id)}
                          />
                          <span>{emp.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="filter-actions">
                    <button className="btn-clear-filters" onClick={clearFilters}>Clear Filters</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <table className="leave-table">
          <thead>
            <tr>
              {leaveView === 'all' && <th>EMPLOYEE</th>}
              {leaveView === 'all' && <th>ROLE</th>}
              <th>LEAVE TYPE</th>
              <th>START DATE</th>
              <th>END DATE</th>
              <th>DAYS</th>
              <th>REASON</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeaves.length === 0 ? (
              <tr><td colSpan={leaveView === 'all' ? '8' : '6'} className="no-data">No leave records found</td></tr>
            ) : (
              filteredLeaves.map(leave => (
                <tr key={leave._id}>
                  {leaveView === 'all' && <td>{leave.user?.name || 'N/A'}</td>}
                  {leaveView === 'all' && <td><span className="role-badge">{leave.user?.role || 'N/A'}</span></td>}
                  <td>{leave.leaveType}</td>
                  <td>{new Date(leave.startDate).toLocaleDateString()}</td>
                  <td>{new Date(leave.endDate).toLocaleDateString()}</td>
                  <td>{leave.days}</td>
                  <td>{leave.reason}</td>
                  <td>
                    <span className={`status-badge status-${leave.status.toLowerCase()}`}>
                      {leave.status}
                    </span>
                    {leave.status === 'Pending' && (
                      <div className="leave-actions">
                        <button
                          className="btn-approve"
                          onClick={() => onApprove(leave._id, 'Approved')}
                        >
                          Approve
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => onApprove(leave._id, 'Rejected')}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const HolidaysTab = ({ holidays, onDelete }) => (
  <div className="holidays-container">
    <div className="holidays-header">
      <h3 className="holidays-title">Company Holidays 2025</h3>
      <p className="holidays-subtitle">Manage organization-wide holidays and observances</p>
    </div>

    <div className="holidays-list">
      {holidays.length === 0 ? (
        <div className="no-data">No holidays found</div>
      ) : (
        holidays.map(holiday => {
          const holidayDate = new Date(holiday.date);
          const dayName = holidayDate.toLocaleDateString('en-US', { weekday: 'long' });
          const isUpcoming = holidayDate > new Date();

          return (
            <div key={holiday._id} className={`holiday-card ${isUpcoming ? 'upcoming' : ''}`}>
              <div className="holiday-date-box">
                <div className="holiday-day">{holidayDate.getDate()}</div>
                <div className="holiday-month">{holidayDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</div>
                <div className="holiday-year">{holidayDate.getFullYear()}</div>
              </div>
              <div className="holiday-details">
                <h4 className="holiday-name">{holiday.name}</h4>
                <div className="holiday-meta">
                  <span className={`holiday-type-badge ${holiday.type.toLowerCase().replace(' ', '-')}`}>
                    {holiday.type}
                  </span>
                  <span className="holiday-day-badge">{dayName}</span>
                  <span className={`holiday-status-badge ${isUpcoming ? 'upcoming' : 'past'}`}>
                    {isUpcoming ? 'Upcoming' : 'Past'}
                  </span>
                </div>
              </div>
              <div className="holiday-actions">
                <button className="btn-icon-edit" title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button className="btn-icon-delete" onClick={() => onDelete(holiday._id)} title="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
);

const MarkAttendanceModal = ({ employees, selectedDate, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    employee: '',
    date: selectedDate,
    status: 'Present',
    punchInTime: '',
    punchOutTime: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Mark Attendance</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Employee *</label>
            <select
              value={formData.employee}
              onChange={(e) => setFormData({ ...formData, employee: e.target.value })}
              required
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Status *</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Late">Late</option>
              <option value="Half Day">Half Day</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
          {formData.status !== 'Absent' && formData.status !== 'On Leave' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Punch In Time</label>
                  <input
                    type="time"
                    value={formData.punchInTime}
                    onChange={(e) => setFormData({ ...formData, punchInTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Punch Out Time</label>
                  <input
                    type="time"
                    value={formData.punchOutTime}
                    onChange={(e) => setFormData({ ...formData, punchOutTime: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Marking...' : 'Mark Attendance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ApplyLeaveModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    leaveType: 'Sick Leave',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      alert('End date cannot be before start date');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Apply Leave</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type *</label>
            <select
              value={formData.leaveType}
              onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
              required
            >
              <option value="Sick Leave">Sick Leave</option>
              <option value="Casual Leave">Casual Leave</option>
              <option value="Vacation Leave">Vacation Leave</option>
              <option value="Maternity Leave">Maternity Leave</option>
              <option value="Paternity Leave">Paternity Leave</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
          {formData.startDate && formData.endDate && (
            <div className="form-info">
              <strong>Total Days:</strong> {calculateDays()} day(s)
            </div>
          )}
          <div className="form-group">
            <label>Reason *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows="4"
              placeholder="Please provide a reason for your leave request"
              required
              minLength="10"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Apply Leave'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const HolidayModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'Public Holiday',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Holiday</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Holiday Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Christmas Day"
              required
            />
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <option value="Public Holiday">Public Holiday</option>
              <option value="Company Holiday">Company Holiday</option>
              <option value="Optional Holiday">Optional Holiday</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Optional description"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Holiday'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditAttendanceModal = ({ record, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    status: record.status,
    punchInTime: record.punchInTime,
    punchOutTime: record.punchOutTime,
    notes: record.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Attendance</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Employee</label>
            <input type="text" value={record.user?.name || 'N/A'} disabled />
          </div>
          <div className="form-group">
            <label>Date</label>
            <input type="text" value={new Date(record.date).toLocaleDateString()} disabled />
          </div>
          <div className="form-group">
            <label>Status *</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Late">Late</option>
              <option value="Half Day">Half Day</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
          {formData.status !== 'Absent' && formData.status !== 'On Leave' && (
            <div className="form-row">
              <div className="form-group">
                <label>Punch In Time</label>
                <input
                  type="time"
                  value={formData.punchInTime}
                  onChange={(e) => setFormData({ ...formData, punchInTime: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Punch Out Time</label>
                <input
                  type="time"
                  value={formData.punchOutTime}
                  onChange={(e) => setFormData({ ...formData, punchOutTime: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Attendance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HRAttendanceNew;
